<!-- PaymentCardModal.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import { usePaymentStore } from "@/stores/usePaymentStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

// ─────────────────────────────────────────────
// PROPS / EMITS
// ─────────────────────────────────────────────
const props = defineProps({
  show: { type: Boolean, required: true },
  paymentId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// ─────────────────────────────────────────────
// ESTADO
// ─────────────────────────────────────────────
const store = usePaymentStore();
const isLoadingData = ref(true);
const payment = ref(null);

// ─────────────────────────────────────────────
// FETCH — watch em paymentId com immediate: true
// (mesmo padrão do SubscriptionCardModal)
// ─────────────────────────────────────────────
watch(
  () => props.paymentId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    payment.value = null;
    try {
      // Tenta cache da store primeiro (evita request extra)
      const cached = store.payments.find((p) => p.paymentId === newId);
      if (cached) {
        payment.value = cached;
      } else {
        const response = await api.get(`/Payments/${newId}`);
        payment.value = response.data;
      }
    } catch (e) {
      console.error("❌ Erro ao carregar payment no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// ─────────────────────────────────────────────
// NORMALIZAÇÃO DE STATUS
// ─────────────────────────────────────────────
function normalizePaymentStatus(status) {
  if (status == null) return "none";
  const s = String(status).trim().toLowerCase();
  const map = {
    none: "none",
    pending: "pending",
    approved: "approved",
    rejected: "rejected",
    cancelled: "cancelled",
    refunded: "refunded",
    0: "none",
    1: "pending",
    2: "approved",
    3: "rejected",
    4: "cancelled",
    5: "refunded",
  };
  return map[s] ?? "none";
}

function getStatusLabel(status) {
  return (
    {
      none: "—",
      pending: "Pendente",
      approved: "Aprovado",
      rejected: "Rejeitado",
      cancelled: "Cancelado",
      refunded: "Estornado",
    }[normalizePaymentStatus(status)] ?? "—"
  );
}

function getStatusClass(status) {
  return (
    {
      none: "badge badge-gray",
      pending: "badge badge-yellow",
      approved: "badge badge-green",
      rejected: "badge badge-red",
      cancelled: "badge badge-red",
      refunded: "badge badge-purple",
    }[normalizePaymentStatus(status)] ?? "badge badge-gray"
  );
}

// ─────────────────────────────────────────────
// CHIP DE MÉTODO DE PAGAMENTO
// ─────────────────────────────────────────────
const paymentMethodChip = computed(() => {
  const raw = (payment.value?.paymentMethod ?? "").toLowerCase().trim();
  const map = {
    courtesy: { label: "Cortesia", icon: "fas fa-gift", cls: "badge-courtesy" },
    pix: { label: "PIX", icon: "fas fa-qrcode", cls: "badge-green" },
    credit_card: {
      label: "Cartão",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    creditcard: {
      label: "Cartão",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    debit_card: {
      label: "Débito",
      icon: "fas fa-credit-card",
      cls: "badge-blue",
    },
    ticket: { label: "Boleto", icon: "fas fa-barcode", cls: "badge-orange" },
    boleto: { label: "Boleto", icon: "fas fa-barcode", cls: "badge-orange" },
    account_money: {
      label: "Saldo MP",
      icon: "fas fa-wallet",
      cls: "badge-yellow",
    },
    free: { label: "Free", icon: "fas fa-star", cls: "badge-gray" },
    trial: { label: "Trial", icon: "fas fa-clock", cls: "badge-yellow" },
    bank_transfer: {
      label: "Transferência",
      icon: "fas fa-university",
      cls: "badge-blue",
    },
  };
  if (!raw)
    return { label: "—", icon: "fas fa-question-circle", cls: "badge-gray" };
  return (
    map[raw] ?? {
      label: payment.value.paymentMethod,
      icon: "fas fa-money-bill-wave",
      cls: "badge-gray",
    }
  );
});

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
function fmt(v) {
  return v != null && v !== "" ? v : "—";
}
function fmtBrl(v) {
  return v != null ? `R$ ${Number(v).toFixed(2)}` : "—";
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="card-modal" :style="panelStyle">
      <!-- HEADER -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">
          Detalhes do Payment
          <span v-if="payment" class="title-id"
            >(ID: {{ payment.paymentId }})</span
          >
        </h3>
        <button
          @click="emit('close')"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <!-- LOADING -->
      <div v-if="isLoadingData" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes...
      </div>

      <!-- CONTEÚDO -->
      <div v-else-if="payment" class="card-body">
        <!-- ── SEÇÃO: Identificação ──────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Identificação</h4>

          <!-- Linha 1: IDs -->
          <div class="info-grid info-grid-3col" style="margin-bottom: 0.5rem">
            <div class="info-item">
              <span class="info-label">ID Payment</span>
              <span class="info-value mono">{{ payment.paymentId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">ID Assinatura</span>
              <span class="info-value mono">{{ payment.subscriptionId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Usuário</span>
              <span class="info-value mono">
                {{ payment.userId }}
                <span v-if="payment.userName" class="id-muted"
                  >({{ payment.userName }})</span
                >
              </span>
            </div>
          </div>

          <!-- Linha 2: Método + Status + MP Payment ID -->
          <div class="info-grid info-grid-3col">
            <div class="info-item info-item-chip">
              <span class="info-label">Método</span>
              <span :class="['badge', paymentMethodChip.cls]">
                <i :class="paymentMethodChip.icon"></i>
                {{ paymentMethodChip.label }}
              </span>
            </div>
            <div class="info-item info-item-chip">
              <span class="info-label">Status</span>
              <span :class="getStatusClass(payment.status)">
                {{ getStatusLabel(payment.status) }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">MP Payment ID</span>
              <span class="info-value mono copyable">{{
                fmt(payment.mpPaymentId)
              }}</span>
            </div>
          </div>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Valores ───────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Valores</h4>
          <div class="info-grid info-grid-4col">
            <div class="info-item">
              <span class="info-label">Valor Base</span>
              <span class="info-value">{{ fmtBrl(payment.baseAmount) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Desconto</span>
              <span class="info-value">{{
                fmtBrl(payment.discountAmount)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Valor Final</span>
              <span class="info-value" style="font-weight: 700">{{
                fmtBrl(payment.finalAmount)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Preço Plano (snap.)</span>
              <span class="info-value">{{
                fmtBrl(payment.planPriceSnapshot)
              }}</span>
            </div>
          </div>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Plano & Cupom ─────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Plano &amp; Cupom</h4>
          <div class="info-grid info-grid-3col">
            <div class="info-item">
              <span class="info-label">Plano (snapshot)</span>
              <span class="info-value">{{
                fmt(payment.planNameSnapshot ?? payment.planName)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Código Cupom</span>
              <span class="info-value mono">{{
                fmt(payment.couponCodeSnapshot)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">% Desconto Cupom</span>
              <span class="info-value">
                {{
                  payment.couponPercentSnapshot != null
                    ? `${payment.couponPercentSnapshot}%`
                    : "—"
                }}
              </span>
            </div>
          </div>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Detalhe de Status ─────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Detalhe de Status</h4>
          <blockquote class="status-quote">
            {{ fmt(payment.statusDetail) }}
          </blockquote>
        </section>

        <hr class="divider" />

        <!-- ── SEÇÃO: Datas ─────────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Datas</h4>
          <div class="info-grid info-grid-2col">
            <div class="info-item">
              <span class="info-label">Criado em</span>
              <span class="info-value">{{
                formatDateTimeForDisplay(payment.createdAt)
              }}</span>
            </div>
          </div>
        </section>
      </div>

      <!-- VAZIO / ERRO -->
      <div v-else class="loading-state">Payment não encontrado.</div>
    </div>
  </div>
</template>

<style scoped>
/* ── CONTAINER ─────────────────────────────────────────────── */
.card-modal {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 680px;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.modal-close-btn .w-5 {
  width: 1.25rem;
  height: 1.25rem;
}

/* ── BODY ──────────────────────────────────────────────────── */
.card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  flex: 1;
}

/* ── SEÇÕES ────────────────────────────────────────────────── */
.card-section {
  padding: 0.25rem 0 0.75rem;
}

.section-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-subtle);
  margin: 0 0 0.6rem 0;
}

.divider {
  border: none;
  border-top: 1px dashed var(--border-color);
  margin: 0 0 0.75rem 0;
}

/* ── GRIDS ─────────────────────────────────────────────────── */
.info-grid {
  display: grid;
  gap: 0.5rem 1rem;
}
.info-grid-2col {
  grid-template-columns: repeat(2, 1fr);
}
.info-grid-3col {
  grid-template-columns: repeat(3, 1fr);
}
.info-grid-4col {
  grid-template-columns: repeat(4, 1fr);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.info-item-chip {
  align-items: flex-start;
}

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.info-value {
  font-size: 0.88rem;
  color: var(--text-dark);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

.id-muted {
  color: var(--text-subtle);
  font-size: 0.78rem;
  margin-left: 2px;
}

.copyable {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: block;
  cursor: default;
}
.copyable:hover {
  overflow: visible;
  white-space: normal;
  word-break: break-all;
}

/* ── BADGES ────────────────────────────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  white-space: nowrap;
}
.badge-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.badge-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.badge-yellow {
  background-color: var(--warning-bg);
  color: #854d0e;
}
.badge-orange {
  background-color: #ffe8cc;
  color: #9c4a00;
}
.badge-blue {
  background-color: #d0ebff;
  color: #1864ab;
}
.badge-purple {
  background-color: #e9d5ff;
  color: #6b21a8;
}
.badge-gray {
  background-color: #f1f3f5;
  color: #495057;
}
.badge-courtesy {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: 1px solid #c4b5fd;
}

/* ── BLOCKQUOTE STATUS DETAIL ──────────────────────────────── */
.status-quote {
  margin: 0;
  padding: 0.4rem 0 0.4rem 0.75rem;
  border-left: 4px solid var(--brand-soft);
  color: var(--text-light);
  font-size: 0.88rem;
  line-height: 1.6;
  white-space: pre-wrap;
  background: transparent;
}

.loading-state {
  flex: 1;
}

.btn .w-5 {
  width: 1.25rem;
  height: 1.25rem;
}
.mr-2 {
  margin-right: 0.5rem;
}
</style>